import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { services } from '../data/services';
import { trackAnalyticsEvent } from '../hooks/useAnalytics';

const ServiceDetailPage = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const service = services.find(s => s.id === serviceId);

  const handleGoBack = () => {
    window.history.back();
  };

  const handleContactClick = () => {
    trackAnalyticsEvent('conversion', { 
      event: 'contact_sales_click', 
      page: `/services/${serviceId}` 
    });
  };

  if (!service) {
    return (
      <div className="min-h-screen bg-primary-bg pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Service Not Found</h1>
          <p className="text-gray-400 mb-8">The service you're looking for doesn't exist.</p>
          <button
            onClick={handleGoBack}
            className="text-primary-accent hover:text-white transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-bg pt-20">
      <div className="container mx-auto px-6 py-20">
        
        {/* Header */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Back Button */}
          <motion.button
            onClick={handleGoBack}
            className="flex items-center space-x-3 text-primary-accent hover:text-white transition-colors duration-300 mb-8"
            whileHover={{ x: -5 }}
            transition={{ duration: 0.2 }}
          >
            <ArrowLeft size={20} />
            <span>Back to Services</span>
          </motion.button>

          {/* Service Title */}
          <h1 className="text-5xl lg:text-7xl font-display font-bold text-white mb-6">
            {service.title}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            {service.description}
          </p>
        </motion.div>

        {/* Coming Soon Content */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Left Column - Service Image */}
          <div className="space-y-6">
            <div className="glass-morphism rounded-2xl p-6 bg-white/5">
              <img
                src={service.image}
                alt={service.title}
                className="w-full h-80 object-cover rounded-xl"
              />
            </div>
            
            {/* Features List */}
            {service.features && (
              <div className="glass-morphism rounded-2xl p-6 bg-white/5">
                <h3 className="text-xl font-semibold text-white mb-4">Key Features</h3>
                <div className="space-y-3">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-primary-video rounded-full" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Coming Soon */}
          <div className="space-y-6">
            <div className="glass-morphism rounded-2xl p-8 bg-white/5">
              <h2 className="text-3xl font-display font-bold text-white mb-6">
                Detailed Information Coming Soon
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6">
                We're working on comprehensive documentation and detailed information 
                for {service.title}. This page will soon include:
              </p>
              
              <div className="space-y-3 mb-8">
                {[
                  'Technical specifications and capabilities',
                  'Implementation guides and best practices',
                  'Case studies and success stories',
                  'API documentation and integration examples',
                  'Pricing and subscription options'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-primary-success rounded-full" />
                    <span className="text-gray-400">{item}</span>
                  </div>
                ))}
              </div>

              {/* Contact CTA */}
              <div className="border-t border-white/10 pt-6">
                <h3 className="text-lg font-semibold text-white mb-3">
                  Need More Information?
                </h3>
                <p className="text-gray-400 mb-4">
                  Contact our team for detailed information about {service.title} 
                  and how it can benefit your organization.
                </p>
                <a
                  href="mailto:info@marvinenergy.com"
                  onClick={handleContactClick}
                  className="inline-flex items-center space-x-2 text-primary-accent hover:text-white transition-colors"
                >
                  <span>Contact Sales Team</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>

            {/* Additional Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="glass-morphism rounded-xl p-4 bg-white/5 text-center">
                <div className="text-2xl font-bold text-primary-video mb-1">24/7</div>
                <div className="text-sm text-gray-400">Support Available</div>
              </div>
              <div className="glass-morphism rounded-xl p-4 bg-white/5 text-center">
                <div className="text-2xl font-bold text-primary-accent mb-1">99.9%</div>
                <div className="text-sm text-gray-400">Uptime SLA</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Related Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-2xl font-display font-bold text-white mb-8">
            Related Services
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services
              .filter(s => s.id !== service.id)
              .slice(0, 3)
              .map((relatedService) => (
                <motion.a
                  key={relatedService.id}
                  href={`/services/${relatedService.id}`}
                  className="glass-morphism rounded-xl p-6 bg-white/5 hover:bg-white/10 transition-all duration-300 block"
                  whileHover={{ scale: 1.02, y: -2 }}
                >
                  <h4 className="text-lg font-semibold text-white mb-2">
                    {relatedService.title}
                  </h4>
                  <p className="text-sm text-gray-400">
                    {relatedService.description.slice(0, 100)}...
                  </p>
                </motion.a>
              ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ServiceDetailPage;

